import { useMemo } from "react";

import {
  resolvePoolLabel,
  useServiceLabel,
  useSetPoolLabel,
} from "@/api/local/labels";
import type { Pool, Replica, Service } from "@/api/types";
import { GLOBAL_FOCUS, type Focus } from "@/workloads/focus";
import { POOL_TABS } from "@/workloads/scope";
import {
  humanShapeLabel,
  isPoolDraft,
  poolShapeSlug,
} from "@/workloads/tree-data";

import { PoolStatusBanner } from "@/components/PoolStatusBanner";

import { DetailShell } from "./DetailShell";
import { PoolAutoscaling } from "./pool/PoolAutoscaling";
import { PoolCost } from "./pool/PoolCost";
import { PoolDashboard } from "./pool/PoolDashboard";
import { PoolEvents } from "./pool/PoolEvents";
import { PoolLogs } from "./pool/PoolLogs";
import { PoolProviders } from "./pool/PoolProviders";
import { PoolReplicas } from "./pool/PoolReplicas";
import { PoolSettings } from "./pool/PoolSettings";
import { PoolSpec } from "./pool/PoolSpec";
import { PoolWorkload } from "./pool/PoolWorkload";

interface Props {
  pool: Pool;
  service: Service;
  replicas: Replica[];
  tab: string;
  setFocusAndTab: (focus: Focus, tab: string) => void;
}

export function PoolDetail({
  pool,
  service,
  replicas,
  tab,
  setFocusAndTab,
}: Props) {
  const poolReplicas = useMemo(
    () => replicas.filter((r) => r.pool_id === pool.id),
    [replicas, pool.id],
  );
  const serviceLabel = useServiceLabel(service.id, service.name);
  const shapeLabel = humanShapeLabel(poolShapeSlug(pool));
  const poolLabel = resolvePoolLabel(pool);
  const setPoolLabel = useSetPoolLabel();
  // Keep the subtitle truthful for draft pools: "P0 · Default shape"
  // would suggest the operator picked the cloud-default shape, but a
  // freshly-inserted "+ pool" hasn't picked anything. Surface that
  // state explicitly so operators are nudged to the Spec tab.
  const subtitle = isPoolDraft(pool)
    ? `pool · P${pool.priority} · draft`
    : `pool · P${pool.priority} · ${shapeLabel}`;
  // The placement summary used to be appended here, but Pools no
  // longer carry a per-pool placement list — the candidate set is
  // resolved from the Provider config inheritance chain at render
  // time and surfaced inside the pool's Spec / Dashboard tabs.
  const breadcrumbTail = isPoolDraft(pool)
    ? "(unconfigured)"
    : shapeLabel;

  return (
    <DetailShell
      tabs={POOL_TABS}
      activeTab={tab}
      onTabChange={(id) => setFocusAndTab({ type: "pool", id: pool.id }, id)}
      badges={{ replicas: poolReplicas.length }}
      navTitle={poolLabel}
      navSubtitle={subtitle}
      onRenameTitle={(next) => {
        void setPoolLabel(pool.id, next);
      }}
      renameHint={`slug stays "${pool.sky_service_name}"`}
      breadcrumb={[
        {
          label: "BackStage",
          onClick: () => setFocusAndTab(GLOBAL_FOCUS, "dashboard"),
        },
        {
          label: serviceLabel,
          onClick: () =>
            setFocusAndTab({ type: "service", id: service.id }, "dashboard"),
        },
        { label: breadcrumbTail },
      ]}
    >
      {/* One honest story above every tab: the server-derived
          status_summary reconciles phase + latest error, so the
          header never shows a calm "Launching" while the pool is
          actually stuck in a capacity-rejection loop. */}
      <PoolStatusBanner pool={pool} />
      {renderTab(tab, pool, service, poolReplicas, setFocusAndTab)}
    </DetailShell>
  );
}

function renderTab(
  tab: string,
  pool: Pool,
  service: Service,
  poolReplicas: Replica[],
  setFocusAndTab: (focus: Focus, tab: string) => void,
) {
  switch (tab) {
    case "spec":
      return <PoolSpec pool={pool} service={service} />;
    case "workload":
      return <PoolWorkload pool={pool} service={service} />;
    case "replicas":
      return (
        <PoolReplicas pool={pool} service={service} replicas={poolReplicas} />
      );
    case "autoscaling":
      return <PoolAutoscaling pool={pool} service={service} />;
    case "cost":
      return <PoolCost pool={pool} />;
    case "providers":
      return <PoolProviders pool={pool} service={service} />;
    case "events":
      return <PoolEvents pool={pool} service={service} />;
    case "logs":
      return <PoolLogs pool={pool} replicas={poolReplicas} />;
    case "settings":
      return (
        <PoolSettings
          pool={pool}
          service={service}
          setFocusAndTab={setFocusAndTab}
        />
      );
    case "dashboard":
    default:
      return <PoolDashboard pool={pool} replicas={poolReplicas} />;
  }
}
